import React, { useState, useContext } from 'react';
import styled from '@emotion/styled';
import { GlobalTokenColor } from 'ui/theme/Color';
import bg from 'components/asset/images/backgroundImg.svg';
import { GButton } from 'components/atom/button';
import { authLoginDev } from 'apis/vars/login/Login';
import LoginInput from 'components/vars/common/LoginInput';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLanguageStore from 'stores/useLanguageStore';
import useSessionStore from 'stores/useSessionStore';
import useAuthStore from 'stores/useAuthStore';
import { MenuContext } from 'App';
import { ValidationBox } from 'components/validation/ValidationBox';
import { MenuContextType } from 'models/admin/Menu';
import { useCommonModal } from 'hooks/useCommonModal';
import MemberResetModal from './MemberResetModal';

interface Props {
  gap?: string;
}

export interface LoginFormData {
  userId: string;
  password?: string;
  publCertKey?: string;
  emlAddr?: string;
  langCd: string;
  emlAuthCd?: string;
  signUpYn?: string;
  sapValidYn?: string;
  emlCertYn?: string;
  dgsVal?: string;
  bzno?: string;
  bznoKey?: string;
  vdcpCtryCd?: string;
  copCd?: string;
}

export interface AdminLoginFormData {
  userId: string;
  langCd: string;
}

const BackLoginPage = () => {
  const { t } = useTranslation();
  const t2 = t as (msgId: string, defVal: string) => string;
  const navigate = useNavigate();

  const { setAccessCode } = useAuthStore();
  const { changeLanguage } = useLanguageStore();
  const { setSession, langCd } = useSessionStore();

  const [loginFormData, setLoginFormData] = useState<LoginFormData>({
    userId: '',
    password: '',
    publCertKey: '',
    emlAddr: '',
    langCd: langCd,
    emlCertYn: 'N',
    dgsVal: '',
    bzno: '',
    bznoKey: '',
    vdcpCtryCd: '',
    copCd: '',
  });
  const [validationErrMap, setValidationErrMap] = useState<Map<string, string>>(new Map());

  const [resetMemberModalOpen, setResetMemberModalOpen] = useState<boolean>(false);

  const menuContext = useContext<MenuContextType>(MenuContext);

  const { openCommonModal } = useCommonModal();
  /**
   * 개발전용 로그인
   */
  const handleClickDevLogin = async () => {
    //
    const userId = loginFormData.userId;
    const response = await authLoginDev({ userId, langCd });

    if (response.successOrNot === 'Y' && response?.data) {
      const session = response.data;
      changeLanguage(session.langCd || 'en');
      setSession(session);
      setAccessCode('INTERNAL'); //TODO 임시 하드코딩

      menuContext.handleMenuChange({ ...menuContext, selectedHeaderMenu: '000139' });

      navigate('/account-regist', { replace: true });
      return;
    }
    // 로그인 실패시 alert 추가
    openCommonModal({
      modalType: 'alert',
      content: '로그인 실패',
    });
  };

  /**
   * 인풋 컴포넌트 체인지 이벤트
   * @param e
   */
  const handleChangeForLoginForm = (e) => {
    if (e.target.id === 'userId') {
      e.target.value = e.target.value.trim().toUpperCase();
    }

    setLoginFormData((prev) => {
      return { ...prev, [e.target.id]: e.target.value };
    });
  };

  const handleClickMemberReset = () => {
    setResetMemberModalOpen(true);
  };

  const handleClickSignUp = () => {
    navigate('/member/regist');
  };

  return (
    <Wrap>
      <Container>
        <TextBox>
          <p>{`지급계좌등록시스템`}</p>
          <p>
            {`(관리자 벤더 코드 로그인)`}
            <br />
          </p>
          <img src="/assets/icon/logo_en_v2.svg" alt="로고" />
        </TextBox>
        <LoginBox>
          <LogoWrap>
            <Title>VARS</Title>
            <SubTitleWrap>
              {langCd === 'ko' || !langCd ? (
                <>
                  <KoSubTitle>{t('지급계좌등록시스템', '지급계좌등록시스템')}</KoSubTitle>
                  <EnSubTitle>Vendor Account Registration System</EnSubTitle>
                </>
              ) : (
                <>
                  <KoSubTitle>Vendor Account Registration System</KoSubTitle>
                  <EnSubTitle>{t('지급계좌등록시스템', '지급계좌등록시스템')}</EnSubTitle>
                </>
              )}
            </SubTitleWrap>
          </LogoWrap>
          <FlexBox gap="24px">
            {/*label={t2('com.label.vendorcdid', '__Vendor Code ID')}*/}
            <FlexBox gap="16px">
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ValidationBox msg={validationErrMap.get('userId')}>
                  <LoginInput
                    id="userId"
                    type="text"
                    label={t2('com.label.vendorcdid', '__Vendor Code ID')}
                    onChange={handleChangeForLoginForm}
                  >
                    <GButton
                      txt={t('com.label.00003', '로그인') ?? ''}
                      sizes="medium"
                      btnstyled="outline"
                      btnColor="normal"
                      hasImg={false}
                      hasTxt={true}
                      style={{ marginTop: '10px', marginLeft: '10px' }}
                      onClick={handleClickDevLogin}
                    />
                  </LoginInput>
                </ValidationBox>
              </div>
              {/* 회원 초기화, 회원 가입 */}
              <Linked>
                <a
                  href="javascript:void(0)"
                  style={{ color: '#1F1F1F', height: '28px' }}
                  onClick={handleClickMemberReset}
                >
                  {t('com.label.00401', '__회원 정보 초기화')}
                </a>
                <a
                  href="javascript:void(0)"
                  style={{ color: '#5B5C5B', fontWeight: '700', height: '28px' }}
                  onClick={handleClickSignUp}
                >
                  {t('com.label.00005', '회원가입')}
                </a>
              </Linked>
            </FlexBox>
          </FlexBox>
        </LoginBox>
      </Container>
      {resetMemberModalOpen && (
        <MemberResetModal
          open={resetMemberModalOpen}
          close={() => setResetMemberModalOpen(false)}
          save={() => console.log()}
        />
      )}
    </Wrap>
  );
};

const Wrap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  background-size: 488px 388px;
  background: ${GlobalTokenColor.ESGrey100} url(${bg}) no-repeat center center;
  justify-content: center;
  min-height: 60vh;
  overflow-y: auto;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  gap: 300px;
  align-items: center;
`;

const TextBox = styled.div`
  width: 580px;
  height: 300px;
  margin-left: 280px;
  p {
    font-weight: 400;
    font-size: 20px;
    line-height: 30px;
    font-family: Spoqa Han Sans Neo;
  }
  p:nth-of-type(1) {
    font-size: 40px;
    font-weight: 400;
    line-height: 60px;
  }
`;

const LoginBox = styled.div`
  width: 468px;
  height: auto;
  background: ${GlobalTokenColor.ESGreyWHite};
  border-radius: 2px;
  padding: 64px;
  img {
    width: 340px;
    height: 68px;
  }
  gap: 32px;
  display: flex;
  flex-direction: column;
`;

const LogoWrap = styled.div`
  width: 340px;
  height: 68px;
  padding: 16px, 0px, 16px, 0px;

  display: flex;
  align-items: center;

  color: #135678;
  // gap: 12px;
`;

const Title = styled.span`
  padding-right: 12px;
  border-right: 1px solid #b9bcbb;

  font-size: 24px;
  font-weight: 700;
  line-height: 36px;
`;

const EngBox = styled.div`
  display: flex;
  gap: 15px;
  p:first-child {
    font-size: 40px;
    font-weight: 700;
    line-height: 60px;
  }
  p:last-child {
    font-weight: 400;
    font-size: 20px;
    line-height: 60px;
    color: #5b5c5b;
    margin-top: 6px;
  }
`;

const SubTitleWrap = styled.div`
  max-width: 300px;
  height: 33px;
  padding-left: 12px;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const KoSubTitle = styled.span`
  line-height: 18px;
  display: inline-block;
  font-weight: 400;
  font-size: 14px;
`;

const EnSubTitle = styled(KoSubTitle)`
  color: #6e706f;
  font-size: 12px;
  font-weight: 300;
`;

const FlexBox = styled.div<Props>`
  width: 100%;
  display: flex;
  flex-direction: column;
  ${(props) => props.gap && `gap: ${props.gap};`}
`;

const Linked = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: Spoqa Han Sans Neo;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
`;

export default BackLoginPage;
